import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { theme } from '../../config/theme';
import { fieldStyles } from '../../config/formStyles';
import {
  formatCurrency,
  formatTasaForDisplay,
  formatComitenteForDisplay,
  formatCuitForDisplay,
} from '../../utils/formatters';
import { formatHoraForDisplay } from '../../utils/dateHelpers';
import { useStackedTable } from '../../hooks/useStackedTable';
import {
  numeroDe,
  toleranciaDe,
  ESTADOS_ABIERTOS,
  MOTIVO_SIN_PEDIDO,
  MOTIVO_CARTERA_PROPIA,
  MOTIVO_SIN_COMITENTE,
} from '../../utils/caucionesConciliacion';
import { CONCILIACION_ASIGNADA, CONCILIACION_IGNORADA } from '../../services/caucionesImportService';

const ETIQUETA_MOTIVO = {
  [MOTIVO_SIN_PEDIDO]: 'Sin pedido cargado',
  [MOTIVO_CARTERA_PROPIA]: 'Cartera propia',
  [MOTIVO_SIN_COMITENTE]: 'CUIT sin comitente',
  [CONCILIACION_IGNORADA]: 'Descartada a mano',
};

const estilos = {
  contenedor: { marginTop: '0.5rem' },
  cabecera: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem',
  },
  tarjetas: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' },
  tarjeta: (color) => ({
    backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`,
    borderLeft: `4px solid ${color}`, borderRadius: theme.borderRadius, padding: '0.7rem 0.9rem',
  }),
  tarjetaValor: { fontFamily: theme.typography.titleFamily, fontSize: '1.5rem', fontWeight: 700, color: theme.colors.primary },
  tarjetaLabel: { fontSize: '0.75rem', color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' },
  seccion: {
    fontFamily: theme.typography.titleFamily, fontSize: '0.85rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em', color: theme.colors.primary,
    borderBottom: `1px solid ${theme.colors.border}`, paddingBottom: '0.4rem',
    margin: '1.5rem 0 0.6rem 0',
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', backgroundColor: theme.colors.surface },
  th: {
    textAlign: 'left', padding: '0.5rem', color: theme.colors.textSecondary, fontWeight: 600,
    borderBottom: `1px solid ${theme.colors.border}`, whiteSpace: 'nowrap',
  },
  td: { padding: '0.5rem', borderBottom: `1px solid ${theme.colors.border}` },
  tdNum: { padding: '0.5rem', borderBottom: `1px solid ${theme.colors.border}`, textAlign: 'right', whiteSpace: 'nowrap' },
  vacio: { fontSize: '0.82rem', color: theme.colors.textSecondary, padding: '0.6rem 0' },
  barra: (porcentaje, color) => ({
    height: '6px', borderRadius: '3px', backgroundColor: theme.colors.border, overflow: 'hidden', minWidth: '70px',
    backgroundImage: `linear-gradient(to right, ${color} ${porcentaje}%, transparent ${porcentaje}%)`,
  }),
};

/**
 * Panel de conciliación de la rueda.
 *
 * Cruza las cauciones de la planilla con las partidas que se importaron de los
 * archivos de BYMA y A3 (colección `caucionesImportadas`) para mostrar lo que
 * falta cerrar: pedidos sin operar, pedidos operados a medias, y operaciones
 * que se ejecutaron sin pedido cargado.
 *
 * Es una vista viva: se recalcula con cada archivo que sube el trader.
 */
const ConciliacionCaucionesPanel = ({ cauciones = [], clientesDb = {}, userRole, onImportar }) => {
  const [importadas, setImportadas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const tablaParciales = useStackedTable({ title: 'Cliente' });
  const tablaSinOperar = useStackedTable({ title: 'Cliente' });
  const tablaSinImputar = useStackedTable({ title: 'Comitente / CUIT' });

  useEffect(() => {
    const q = query(collection(db, 'caucionesImportadas'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setImportadas(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setCargando(false);
      },
      (error) => {
        console.error('Error al obtener las cauciones importadas:', error);
        setCargando(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const partidasPorCaucion = useMemo(() => {
    const mapa = new Map();
    importadas.forEach((partida) => {
      if (!partida.caucionId) return;
      if (!mapa.has(partida.caucionId)) mapa.set(partida.caucionId, []);
      mapa.get(partida.caucionId).push(partida);
    });
    return mapa;
  }, [importadas]);

  /** Pedidos abiertos: nada operado, o operado por debajo de lo pedido. */
  const abiertas = useMemo(() => {
    return cauciones
      .filter((c) => ESTADOS_ABIERTOS.includes(c.estado))
      .map((c) => {
        const monto = numeroDe(c.monto);
        const operado = numeroDe(c.montoOperado);
        const partidas = partidasPorCaucion.get(c.id) || [];
        const avance = monto > 0 ? Math.min(100, (operado / monto) * 100) : 0;
        return {
          caucion: c,
          monto,
          operado,
          pendiente: Math.max(0, monto - operado),
          avance,
          partidas,
          esParcial: operado > 0 && (monto <= 0 || operado < monto - toleranciaDe(monto)),
        };
      })
      .sort((a, b) => b.pendiente - a.pendiente);
  }, [cauciones, partidasPorCaucion]);

  const parciales = useMemo(() => abiertas.filter((f) => f.esParcial), [abiertas]);
  const sinOperar = useMemo(() => abiertas.filter((f) => f.operado <= 0), [abiertas]);

  const sinImputar = useMemo(
    () => importadas.filter((p) => !p.caucionId).sort((a, b) => numeroDe(b.capital) - numeroDe(a.capital)),
    [importadas]
  );

  const porMercado = useMemo(() => {
    const mapa = new Map();
    importadas.forEach((p) => {
      const clave = p.mercado || '—';
      if (!mapa.has(clave)) mapa.set(clave, { mercado: clave, partidas: 0, imputadas: 0 });
      const item = mapa.get(clave);
      item.partidas += 1;
      if (p.estadoConciliacion === CONCILIACION_ASIGNADA) item.imputadas += 1;
    });
    return [...mapa.values()].sort((a, b) => a.mercado.localeCompare(b.mercado));
  }, [importadas]);

  const nombreDe = (nroComitente, cliente) =>
    cliente || clientesDb[nroComitente] || formatComitenteForDisplay(nroComitente) || '—';

  if (cargando) return <p style={estilos.vacio}>Cargando conciliación…</p>;

  const hayImportaciones = importadas.length > 0;

  return (
    <div style={estilos.contenedor}>
      <div className="axon-wrap" style={estilos.cabecera}>
        <div>
          <div style={{ fontFamily: theme.typography.titleFamily, fontSize: '1.1rem', fontWeight: 600, color: theme.colors.primary }}>
            Conciliación de la rueda
          </div>
          <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>
            {hayImportaciones
              ? `${importadas.length} partida(s) importada(s)${porMercado.length ? ` · ${porMercado.map((m) => `${m.mercado}: ${m.partidas}`).join(' · ')}` : ''}`
              : 'Todavía no se importó ningún archivo de operaciones en esta rueda.'}
          </div>
        </div>
        {userRole === 'Trader' && onImportar && (
          <button type="button" style={fieldStyles.button} onClick={onImportar}>
            Importar operaciones
          </button>
        )}
      </div>

      <div style={estilos.tarjetas}>
        <div style={estilos.tarjeta('#DC2626')}>
          <div style={estilos.tarjetaValor}>{sinOperar.length}</div>
          <div style={estilos.tarjetaLabel}>Sin operar</div>
        </div>
        <div style={estilos.tarjeta('#F59E0B')}>
          <div style={estilos.tarjetaValor}>{parciales.length}</div>
          <div style={estilos.tarjetaLabel}>Operadas a medias</div>
        </div>
        <div style={estilos.tarjeta('#6366F1')}>
          <div style={estilos.tarjetaValor}>{sinImputar.length}</div>
          <div style={estilos.tarjetaLabel}>Sin pedido</div>
        </div>
        <div style={estilos.tarjeta('#10B981')}>
          <div style={estilos.tarjetaValor}>
            {importadas.filter((p) => p.estadoConciliacion === CONCILIACION_ASIGNADA).length}
          </div>
          <div style={estilos.tarjetaLabel}>Partidas imputadas</div>
        </div>
      </div>

      <div style={estilos.seccion}>Operadas a medias ({parciales.length})</div>
      {parciales.length === 0 ? (
        <p style={estilos.vacio}>No hay cauciones a medio operar.</p>
      ) : (
        <table ref={tablaParciales.ref} className={tablaParciales.className} style={estilos.tabla}>
          <thead>
            <tr>
              <th style={estilos.th}>Cliente</th>
              <th style={estilos.th}>Op.</th>
              <th style={estilos.th}>Moneda</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Pedido</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Operado</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Falta</th>
              <th style={estilos.th}>Avance</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Tasa op.</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Part.</th>
              <th style={estilos.th}>Operador</th>
            </tr>
          </thead>
          <tbody>
            {parciales.map(({ caucion, monto, operado, pendiente, avance, partidas }) => (
              <tr key={caucion.id}>
                <td style={estilos.td}>{nombreDe(caucion.nroComitente, caucion.cliente)}</td>
                <td style={estilos.td}>{caucion.operacion}</td>
                <td style={estilos.td}>{caucion.moneda}</td>
                <td style={estilos.tdNum}>{monto > 0 ? formatCurrency(monto, caucion.moneda) : 'a definir'}</td>
                <td style={estilos.tdNum}>{formatCurrency(operado, caucion.moneda)}</td>
                <td style={{ ...estilos.tdNum, color: '#92400E', fontWeight: 600 }}>
                  {formatCurrency(pendiente, caucion.moneda)}
                </td>
                <td style={estilos.td}>
                  <div style={estilos.barra(avance, '#F59E0B')} title={`${avance.toFixed(1)}%`} />
                </td>
                <td style={estilos.tdNum}>{formatTasaForDisplay(numeroDe(caucion.tasaOperada))}</td>
                <td style={estilos.tdNum}>{partidas.length}</td>
                <td style={estilos.td}>{caucion.operador || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={estilos.seccion}>Sin operar ({sinOperar.length})</div>
      {sinOperar.length === 0 ? (
        <p style={estilos.vacio}>Todos los pedidos de la planilla tienen al menos una partida operada.</p>
      ) : (
        <table ref={tablaSinOperar.ref} className={tablaSinOperar.className} style={estilos.tabla}>
          <thead>
            <tr>
              <th style={estilos.th}>Cliente</th>
              <th style={estilos.th}>Op.</th>
              <th style={estilos.th}>Moneda</th>
              <th style={estilos.th}>Mercado</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Pedido</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Tasa</th>
              <th style={estilos.th}>Comercial</th>
              <th style={estilos.th}>Estado</th>
              <th style={estilos.th}>Cargada</th>
            </tr>
          </thead>
          <tbody>
            {sinOperar.map(({ caucion, monto }) => (
              <tr key={caucion.id}>
                <td style={estilos.td}>{nombreDe(caucion.nroComitente, caucion.cliente)}</td>
                <td style={estilos.td}>{caucion.operacion}</td>
                <td style={estilos.td}>{caucion.moneda}</td>
                <td style={estilos.td}>{caucion.mercado}</td>
                <td style={estilos.tdNum}>{monto > 0 ? formatCurrency(monto, caucion.moneda) : 'a definir'}</td>
                <td style={estilos.tdNum}>
                  {numeroDe(caucion.tasa) > 0 ? formatTasaForDisplay(caucion.tasa) : 'a definir'}
                </td>
                <td style={estilos.td}>{caucion.comercial || '—'}</td>
                <td style={estilos.td}>{caucion.estado}</td>
                <td style={estilos.td}>{formatHoraForDisplay(caucion.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={estilos.seccion}>Operado sin pedido cargado ({sinImputar.length})</div>
      {sinImputar.length === 0 ? (
        <p style={estilos.vacio}>Todas las operaciones importadas quedaron imputadas a una caución.</p>
      ) : (
        <table ref={tablaSinImputar.ref} className={tablaSinImputar.className} style={estilos.tabla}>
          <thead>
            <tr>
              <th style={estilos.th}>Mercado</th>
              <th style={estilos.th}>Comitente / CUIT</th>
              <th style={estilos.th}>Op.</th>
              <th style={estilos.th}>Moneda</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Capital</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Tasa</th>
              <th style={{ ...estilos.th, textAlign: 'right' }}>Días</th>
              <th style={estilos.th}>Motivo</th>
              <th style={estilos.th}>Id externo</th>
            </tr>
          </thead>
          <tbody>
            {sinImputar.map((partida) => (
              <tr key={partida.id}>
                <td style={estilos.td}>{partida.mercado}</td>
                <td style={estilos.td}>
                  {partida.nroComitente
                    ? nombreDe(partida.nroComitente, '')
                    : partida.cuit
                      ? formatCuitForDisplay(partida.cuit)
                      : '—'}
                </td>
                <td style={estilos.td}>{partida.operacion}</td>
                <td style={estilos.td}>{partida.moneda}</td>
                <td style={estilos.tdNum}>{formatCurrency(numeroDe(partida.capital), partida.moneda)}</td>
                <td style={estilos.tdNum}>{formatTasaForDisplay(numeroDe(partida.tasa))}</td>
                <td style={estilos.tdNum}>{numeroDe(partida.dias)}</td>
                <td style={estilos.td}>{ETIQUETA_MOTIVO[partida.estadoConciliacion] || partida.estadoConciliacion}</td>
                <td style={{ ...estilos.td, fontSize: '0.72rem', color: theme.colors.textSecondary }}>
                  {partida.idExterno}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ConciliacionCaucionesPanel;
